import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Zones d\'intervention', item: 'https://fenetre-paris15.fr/zones-intervention-paris-15/' },
  { name: 'Grenelle' },
]);

export default function QuartierGrenellePage() {
  return (
    <Layout>
      <SEOHead
        title="Réparation Fenêtre Quartier Grenelle (Paris 15) | DF PRO"
        description="Réparation de fenêtres et volets dans le quartier Grenelle (Paris 15ᵉ). 24/48h, devis gratuit. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/quartier-grenelle/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><Link to="/zones-intervention-paris-15/" className="hover:text-brand-accent">Zones</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Grenelle</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark mb-6">Réparation de fenêtres dans le quartier Grenelle — Paris 15ᵉ</h1>

          {/* Image Grenelle */}
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8">
            <img
              src="/images/dfpro-grenelle.webp"
              alt="Quartier Grenelle — bord de Seine mêlant haussmannien et immeubles récents, DF PRO Paris 15"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          <div className="prose prose-sm max-w-none text-brand-text/70 leading-relaxed space-y-4">
            <p>
              Le quartier Grenelle est l'un des <strong>plus vastes et diversifiés du 15ᵉ arrondissement</strong>. S'étendant de la Seine (au niveau du pont Mirabeau et du pont de Grenelle) jusqu'à la place Dupleix, il englobe une mosaïque de types d'habitat : immeubles haussmanniens anciens le long des grands boulevards, résidences récentes en bord de Seine, et un nombre important d'immeubles de bureaux dans sa partie nord. La desserte est excellente avec les lignes de métro 6 (Dupleix, Bir-Hakeim), 8 (La Motte-Picquet-Grenelle) et 10 (Charles Michels, Avenue Émile Zola).
            </p>
            <p>
              Cette diversité architecturale se traduit par une <strong>grande variété de menuiseries</strong> : fenêtres bois d'origine dans le bâti haussmannien, menuiseries PVC dans les immeubles rénovés des années 1980-2000, et aluminium contemporain dans les constructions récentes. Chaque type de bâti a ses problématiques spécifiques en matière de quincaillerie.
            </p>

            <h2 className="font-heading text-xl font-bold text-brand-dark mt-8 mb-4">Pannes selon le type de bâti à Grenelle</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Immeubles haussmanniens</strong> (rue de Grenelle, rue du Commerce) : crémones d'époque qui se grippent, paumelles à visser fatiguées, fenêtres bois qui gonflent avec l'humidité du fleuve. Nous restaurons ces mécanismes sans modifier l'esthétique.</li>
              <li><strong>Résidences récentes</strong> (quai de Grenelle, rue Émeriau) : quincaillerie PVC moderne, oscillo-battants, joints d'étanchéité. Les pannes concernent surtout les crémones multipoints et les compas.</li>
              <li><strong>Immeubles de bureaux</strong> (secteur Dupleix - Bir-Hakeim) : menuiseries aluminium très sollicitées, baies coulissantes, ferme-portes. L'entretien régulier est indispensable.</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-brand-dark mt-8 mb-4">L'impact de la proximité de la Seine</h2>
            <p>
              La proximité immédiate de la Seine a un impact direct sur les menuiseries du quartier Grenelle. L'<strong>humidité ambiante</strong> accélère le vieillissement des joints, favorise le gonflement du bois et peut provoquer une corrosion prématurée des pièces métalliques non protégées. Nous recommandons un contrôle annuel de la quincaillerie pour les logements situés à moins de 200 mètres du fleuve.
            </p>

            <div className="bg-brand-mint/30 rounded-xl p-6 mt-8">
              <h2 className="font-heading text-xl font-bold text-brand-dark mb-3">Une fenêtre à réparer à Grenelle ?</h2>
              <p className="text-brand-text/70 text-sm mb-4">DF PRO connaît parfaitement le quartier. Diagnostic gratuit, intervention rapide.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-8 pt-6 border-t border-gray-200">
              <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline text-sm">← Toutes les zones</Link>
              <Link to="/reparation-fenetre-bois-paris-15/" className="text-brand-accent hover:underline text-sm">Fenêtres Bois</Link>
              <Link to="/reparation-fenetre-pvc-paris-15/" className="text-brand-accent hover:underline text-sm">Fenêtres PVC</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}